/*

mark strings
extract strings
load translations

bundle language inside JS file?
path lang prefixes
mark url paths
get translated paths in sitemaps/api?

make related tags responsive:
https://tenor.com/search/movies-gifs
*/
import {autobind} from 'core-decorators';

import format from 'string-format';
import Gettext from 'node-gettext';

import {removeEmptyStrings} from './util';
import rootScope from './services/rootScope';

import localePathRedirectMap from './locales-redirect-map.json' with { type: 'json' };
import localesSupported from './locales-supported.json' with { type: 'json' };

export {localePathRedirectMap, localesSupported};

export const domain = 'messages';


export type TrimmedTranslationRecord = Record<string, Record<string, string>>;

export interface Translations {
    po: TrimmedTranslationRecord;
    tos: string;
}

export function langOrLocaleIncluded(
    langOrLocale: string, list: string[]): boolean {
    const lang = langOrLocale.substr(0, 2);
    if (list.includes(lang)) {
        return true;
    }
    if (lang !== langOrLocale && list.includes(langOrLocale)) {
        return true;
    }
    return false;
}

export const langRTLList = [
    'ar',
    'dv',
    'en_XA',
    'en_XC',
    'fa',
    'he',
    'ku',
    'ur',
];
export const localeInternalList = [
    'ar',
    'dv',
    'en_XA',
    'en_XC',
    'fa',
    'he',
    'ku',
    'ur',
];
export const langDirDenyList = [
    'ff',
    'ha',
    'ks',
    'ps',
    'yi',
];

/**
 * Enum for language direction.
 * @readonly
 * @enum {string}
 */
export const LANG_DIR = {
    LTR: 'ltr',
    RTL: 'rtl',
};

export function getLocaleDir(locale: string): valueof<typeof LANG_DIR> {
    for (const lang of langRTLList) {
        if (locale.startsWith(lang)) {
            return LANG_DIR.RTL;
        }
    }
    return LANG_DIR.LTR;
}

export class TranslationService {
    locales: string[];
    tos: Record<string, string>;
    gt: Gettext;
    localizedPaths: Record<string, string>;
    localizedPathsRev: Record<string, string>;

    constructor() {
        this.locales = [];
        this.tos = {};
        this.gt = new Gettext();
        this.localizedPaths = {};
        this.localizedPathsRev = {};
    }
    @autobind
    gettextSub(msg: string, kwargs?: object): string {
        let s = this.gt.gettext(msg);
        if (kwargs) {
            s = format(s, kwargs);
        }
        return s;
    }
    /**
     * Used when JSX components are contained within text blocks in need of
     * translation.
     */
    @autobind
    gettextSubComponent(
        msg: string, kwargs: Record<string, any>): any[] {
        const vals = [];
        const l = [];

        const s = this.gt.gettext(msg).replace(/\{(\w+)\}/g, function(m, p1) {
            if (kwargs[p1]) {
                vals.push(kwargs[p1]);
                return `{placeholder}`;
            }
            return m;
        }).split('{placeholder}');

        for (let i = 0; i < s.length; i++) {
            l.push(s[i]);
            if (i < vals.length) {
                l.push(vals[i]);
            }
        }

        return l;
    }
    @autobind
    setLocalizedPaths(newMapping: Record<string, string>) {
        this.localizedPaths = newMapping;
        this.localizedPathsRev = {};
        for (const [key, val] of Object.entries(newMapping)) {
            this.localizedPathsRev[val] = key;
        }
    }
    @autobind
    islocalizeUrlPath(path: string): boolean {
        if (this.gt.locale && this.gt.locale !== 'en' &&
            path.startsWith('/') &&
            !path.startsWith(`/${this.getLocaleUrlPrefix()}/`)) {
            return false;
        }
        return true;
    }
    @autobind
    shouldWarnUnlocalizedUrl(url: string): boolean {
        return (process.env.BROWSER && !url.startsWith('/assets') &&
            !url.startsWith('/gifapi') && !this.islocalizeUrlPath(url));
    }
    @autobind
    localizeUrlPath(path: string): string {
        if (this.gt.locale && this.gt.locale !== 'en') {
            let query;
            [path, query] = path.split('?', 2);
            path = `/${this.getLocaleUrlPrefix()}${path}`;
            path = path.replace(/(\/[^/]+\/)([^/]+)/, (m, p1, p2) => {
                return `${p1}${this.localizedPaths[p2] || ''}`;
            });
            if (query) {
                path = `${path}?${query}`;
            }
            return path;
        }
        return path;
    }
    @autobind
    delocalizeUrlPath(path: string): string {
        if (this.gt.locale && this.gt.locale !== 'en') {
            // Presumes locale in path is current locale.
            return path
                .replace(/\/([^/]+)/, '')
                .replace(/\/([^/]+)/, (m, p1) => {
                    return `/${this.localizedPathsRev[p1]}`;
                });
        }
        return path;
    }
    @autobind
    hydrateTrimmedTranslations(trimmedTranslations: TrimmedTranslationRecord) {
        const obj = {};
        for (const [msgctxt, val] of Object.entries(trimmedTranslations)) {
            obj[msgctxt] = {};
            for (const [msgid, msgstr] of Object.entries(val)) {
                obj[msgctxt][msgid] = {
                    msgid,
                    msgctxt,
                    msgstr: [msgstr],
                };
            }
        }
        return {translations: obj};
    }
    @autobind
    addTranslations(locale: string, translations: Translations) {
        this.locales.push(locale);
        this.gt.addTranslations(
            locale,
            domain,
            translations &&
                this.hydrateTrimmedTranslations(translations.po));
        this.tos[locale] = translations?.tos;
    }
    @autobind
    parseURLSearchTags(s: string, checkPath?: boolean): string[] {
        const searchPath = this.localizeUrlPath('/search/');
        if (checkPath && s.substr(0, searchPath.length) !== searchPath) {
            return [];
        }
        return removeEmptyStrings(s
            .replace(searchPath as string, '')
            .replace(rootScope.searchEndRegEx, '')
            .split(rootScope.searchURLSep));
    }
    @autobind
    parseURLSearchMediaType(s: string): string {
        if (s.match(`${rootScope.searchEndStickers}$`)) {
            return 'stickers';
        } else if (s.match(`${rootScope.searchEndMemes}$`)) {
            return 'memes';
        } else {
            return 'gifs';
        }
    }
    @autobind
    getLocaleUrlPrefix(): string {
        return this.gt.locale.replace('_', '-');
    }
    @autobind
    getTOS(): string {
        return this.tos[this.gt.locale];
    }
    @autobind
    localizeHlParam(text: string): string {
        return text.
            replaceAll('hl=en-US', 'hl=en').
            replaceAll('hl=en', `hl=${this.gt.locale.replace('_', '-')}`);
    }
}
